import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { SwatchLabel } from "@app/projects/network-assessment/multi-map-export/components/SwatchLabel";
import { MultiMapExportContentGroup } from "@app/projects/network-assessment/multi-map-export/interfaces";
import { groupCheckboxState, toggleGroup, toggleOption } from "@app/projects/network-assessment/multi-map-export/utils/mapContentSelection";

export interface SwatchCheckboxGroupProps<TValue> {
  /** The order the emitted selection is sorted into, so state never depends on click order. */
  canonicalOrder: readonly TValue[];
  /** Already filtered for what this user may see, so the parent's state is derived from visible leaves only. */
  group: MultiMapExportContentGroup<TValue>;
  onChange: (selectedValues: TValue[]) => void;
  selectedValues: readonly TValue[];
  /** QESCheckbox uses its testId as the element id, so each consuming section supplies its own namespace. */
  testIdPrefix: string;
}

/** Shared by the Map Content and Adequacy Filter sections, so every group behaves and measures identically. */
export const SwatchCheckboxGroup = <TValue, >({
  canonicalOrder,
  group,
  onChange,
  selectedValues,
  testIdPrefix,
}: SwatchCheckboxGroupProps<TValue>): React.ReactElement => {
  const checkboxState = groupCheckboxState(group, selectedValues);

  return (
    <div className="multi-map-export__swatch-checkbox-group">
      {/* The parent carries no swatch: it toggles a group, it is not a layer or outcome of its own. */}
      <QESCheckbox
        checked={checkboxState === CheckboxStateEnum.Checked}
        containerClassName="multi-map-export__swatch-checkbox"
        indeterminate={checkboxState === CheckboxStateEnum.Indeterminate}
        label={group.label}
        onChange={(event) => onChange(toggleGroup(selectedValues, group, event.target.checked, canonicalOrder))}
        testId={`${testIdPrefix}-group--${group.key}`}
      />
      <div className="multi-map-export__swatch-checkbox-options">
        {group.options.map((option) => (
          <QESCheckbox
            checked={selectedValues.includes(option.value)}
            containerClassName="multi-map-export__swatch-checkbox"
            key={String(option.value)}
            label={<SwatchLabel borderColor={option.swatchBorderColor} color={option.swatchColor} iconName={option.swatchIcon} label={option.label} />}
            onChange={(event) => onChange(toggleOption(selectedValues, option.value, event.target.checked, canonicalOrder))}
            testId={`${testIdPrefix}-option--${option.value}`}
          />
        ))}
      </div>
    </div>
  );
};

export default SwatchCheckboxGroup;
